import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { useAuth, homeFor } from "../lib/auth";
import { useLang } from "../lib/i18n";
import { LangToggle } from "../components/LangToggle";
import { useAppTitle } from "../components/AppShell";
import { OtpForm } from "../components/OtpForm";

const startGoogle = (cfg) => {
  const state = crypto.randomUUID();
  sessionStorage.setItem("oauth_state", state);
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUri = window.location.origin + "/auth/google";
  const params = new URLSearchParams({
    client_id: cfg.client_id, redirect_uri: redirectUri, response_type: "code",
    scope: "openid email profile", state, prompt: "select_account", hd: cfg.allowed_domain,
  });
  window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
};

const PasswordLogin = () => {
  const { setUser } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [challenge, setChallenge] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/password/login", form);
      if (data.otp_required) setChallenge(data.challenge_id);
      else { setUser(data); navigate(homeFor(data)); }
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  if (challenge) return <div className="mt-8"><OtpForm challengeId={challenge} onBack={() => setChallenge("")} /></div>;
  return (
    <form onSubmit={submit} className="mt-8 space-y-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid="password-login-form">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("Owner backup sign-in")}</p>
      <input dir="ltr" type="email" required autoComplete="username" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
        placeholder={t("Email")} data-testid="password-login-email" className="h-12 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-slate-900" />
      <input dir="ltr" type="password" required autoComplete="current-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
        placeholder={t("Password")} data-testid="password-login-password" className="h-12 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-slate-900" />
      <button type="submit" disabled={busy} data-testid="password-login-submit"
        className="btn-press min-h-[48px] w-full rounded-xl bg-slate-900 text-sm font-semibold text-white disabled:opacity-60">{t("Sign in")}</button>
    </form>
  );
};

const DevLogin = () => {
  const { setUser } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/auth/dev-login", { email, token });
      setUser(data);
      navigate(homeFor(data));
    } catch (err) { toast.error(errorMessage(err)); }
  };
  return (
    <form onSubmit={submit} className="mt-8 space-y-2 rounded-2xl border border-dashed border-amber-300 bg-amber-50 p-4" data-testid="dev-login-form">
      <p className="text-xs font-bold uppercase tracking-wider text-amber-700">{t("Dev login (preview only)")}</p>
      <input dir="ltr" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email" data-testid="dev-login-email"
        className="h-11 w-full rounded-xl border border-amber-200 bg-white px-3 font-mono text-sm" />
      <input dir="ltr" type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="dev token" data-testid="dev-login-token"
        className="h-11 w-full rounded-xl border border-amber-200 bg-white px-3 font-mono text-sm" />
      <button type="submit" data-testid="dev-login-submit" className="h-11 w-full rounded-xl bg-amber-600 text-sm font-semibold text-white">{t("Sign in")}</button>
    </form>
  );
};

export default function Login({ backup = false }) {
  const { user, loading } = useAuth();
  const { t, lang } = useLang();
  useAppTitle();
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/auth/config").then(({ data }) => setCfg(data)); }, []);
  if (!loading && user) return <Navigate to={homeFor(user)} replace />;

  return (
    <div className="flex min-h-screen flex-col bg-alabaster">
      <div className="flex justify-end p-4"><LangToggle /></div>
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-6 pb-20">
        <img src="/brand/inception-black.png" alt="Inception" className="mb-12 h-7 w-auto self-start" data-testid="login-logo" />
        <h1 className={`text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl ${lang === "ar" ? "font-arabic" : "font-display"}`} data-testid="login-title">
          {t("sign_in_title")}
        </h1>
        <p className="mt-4 text-base leading-relaxed text-slate-600">{t("sign_in_body")}</p>
        {cfg?.google_enabled ? (
          <button onClick={() => startGoogle(cfg)} data-testid="google-signin-button"
            className="btn-press mt-10 flex min-h-[58px] items-center justify-center gap-3 rounded-2xl bg-slate-900 px-6 font-semibold text-white hover:bg-slate-800">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white font-display text-sm font-extrabold text-slate-900">G</span>
            {t("sign_in")}
          </button>
        ) : cfg && (
          <p className="mt-10 rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600" data-testid="google-not-configured">{t("google_not_configured")}</p>
        )}
        {cfg?.allowed_domain && (
          <p className="mt-4 text-xs text-slate-500" data-testid="domain-lock-indicator">
            <span dir="ltr">@{cfg.allowed_domain}</span>
          </p>
        )}
        {cfg?.dev_login_enabled && <DevLogin />}
        {cfg && backup && <PasswordLogin />}
      </div>
    </div>
  );
}
